import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { format } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
  Button,
  Stack,
  Divider,
  IconButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { ChatRequests, AcceptChatRequest } from "../../services/chatService";
import { useNavigate } from 'react-router-dom';
import VerifiedBadge from '../../components/common/VerifiedBadge';

const ChatRequestList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [chatRooms, setChatRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const formatDate = (dateString) => {
    const locales = { en: enUS, tr: tr };
    const locale = locales[i18n.language] || enUS;
    return format(new Date(dateString), 'PPP', { locale });
  };

  const fetchRequests = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await ChatRequests();
      if (response?.statusCode === 200) {
        setChatRooms(Array.isArray(response.data) ? response.data : []);
      } else if (response === null) {
        setChatRooms([]);
      }
    } catch (error) {
      console.error('Error fetching chat requests:', error);
      toast.error(t('chat.requestError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequest = async (id, type) => {
    setIsLoading(true);
    try {
      const response = await AcceptChatRequest({ id, type });
      if (response.statusCode === 200) {
        toast.success(type ? t('chat.requestAccepted') : t('chat.requestRejected'));
        fetchRequests();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error('Error handling chat request:', error);
      toast.error(t('chat.requestError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca açılışta
  }, []);

  if (isLoading && chatRooms.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        mb: 3,
        gap: 2
      }}>
        <IconButton
          onClick={() => navigate('/chat')}
          sx={{ color: 'text.primary' }}
          aria-label={t('common.back')}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {t('chat.requests')}
        </Typography>
      </Box>

      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {chatRooms.map((room) => (
          <React.Fragment key={room.id}>
            <ListItem
              alignItems="flex-start"
              sx={{
                py: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemAvatar>
                <Avatar
                  src={room.profileImage}
                  alt={room.name}
                />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="subtitle1" color="text.primary">
                    {room.name}
                    {room.isIdentityVerified === true && (
                      <>
                        {' '}
                        <VerifiedBadge verified size={16} />
                      </>
                    )}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(room.date)}
                  </Typography>
                }
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleRequest(room.cid, true)}
                  disabled={isLoading}
                >
                  {t('chat.accept')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleRequest(room.cid, false)}
                  disabled={isLoading}
                >
                  {t('chat.reject')}
                </Button>
              </Stack>
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>

      {chatRooms.length === 0 && !isLoading && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            {t('chat.noRequests')}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ChatRequestList; 